import React, { useMemo } from "react";
import {
  estimateCartCouponSavingsCents,
  pickBestValueCouponCode,
  resolveCartCouponDisplay,
} from "../../../shared/cart-discount-coupon-offer.js";

const PercentBadgeIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
    <circle cx="14" cy="14" r="12" fill="#111827" />
    <circle cx="10.4" cy="10.4" r="1.55" fill="#fff" />
    <circle cx="17.6" cy="17.6" r="1.55" fill="#fff" />
    <path
      d="M18.4 9.6L9.6 18.4"
      stroke="#fff"
      strokeWidth="1.9"
      strokeLinecap="round"
    />
  </svg>
);

function resolveCouponDescription(coupon, display) {
  const candidates = [
    coupon?.description,
    coupon?.summary,
    coupon?.title,
    display.headline,
  ];
  for (const value of candidates) {
    const text = String(value || "").trim();
    if (!text) continue;
    if (text.toLowerCase() === String(display.code || "").toLowerCase()) continue;
    return text;
  }
  return "Discount available on eligible items.";
}

export default function CartCouponSuggestionList({
  coupons = [],
  cart = null,
  formatMoney,
  selectedCode = "",
  onSelect,
  onApply,
  disabled = false,
  isApplied = () => false,
  applyingCode = "",
}) {
  const bestCode = useMemo(
    () => pickBestValueCouponCode(coupons, cart),
    [coupons, cart]
  );

  if (!Array.isArray(coupons) || coupons.length === 0) return null;

  return (
    <div className="weu-custom-cart-drawer__coupon-list">
      <p className="weu-custom-cart-drawer__coupon-list-label">Coupons</p>
      <div className="weu-custom-cart-drawer__coupon-options">
        {coupons.map((coupon) => {
          const display = resolveCartCouponDisplay(coupon);
          const code = display.code;
          const isSelected =
            String(selectedCode).trim().toLowerCase() ===
            String(code).trim().toLowerCase();
          const applied = isApplied(code);
          const isBest =
            bestCode &&
            String(bestCode).trim().toLowerCase() ===
              String(code).trim().toLowerCase();
          const savingsCents = estimateCartCouponSavingsCents(cart, coupon);
          const description = resolveCouponDescription(coupon, display);
          const busy =
            applyingCode &&
            String(applyingCode).trim().toLowerCase() ===
              String(code).trim().toLowerCase();

          return (
            <article
              key={code}
              className={`weu-custom-cart-drawer__coupon-ticket${
                isSelected ? " weu-custom-cart-drawer__coupon-ticket--active" : ""
              }${applied ? " weu-custom-cart-drawer__coupon-ticket--applied" : ""}`}
            >
              {isBest ? (
                <span className="weu-custom-cart-drawer__coupon-ticket-badge">
                  BEST VALUE
                </span>
              ) : null}

              <div className="weu-custom-cart-drawer__coupon-ticket-top">
                <div className="weu-custom-cart-drawer__coupon-ticket-main">
                  <span className="weu-custom-cart-drawer__coupon-ticket-icon">
                    <PercentBadgeIcon />
                  </span>
                  <div className="weu-custom-cart-drawer__coupon-ticket-copy">
                    <span className="weu-custom-cart-drawer__coupon-ticket-code">
                      {code}
                    </span>
                    {savingsCents > 0 && typeof formatMoney === "function" ? (
                      <span className="weu-custom-cart-drawer__coupon-ticket-save">
                        Save {formatMoney(savingsCents)}
                      </span>
                    ) : display.headline &&
                      display.headline.toLowerCase() !== code.toLowerCase() ? (
                      <span className="weu-custom-cart-drawer__coupon-ticket-save">
                        {display.headline}
                      </span>
                    ) : null}
                  </div>
                </div>
                {applied ? (
                  <span className="weu-custom-cart-drawer__applied-badge">Applied</span>
                ) : (
                  <button
                    type="button"
                    className="weu-custom-cart-drawer__coupon-ticket-apply"
                    disabled={disabled || busy}
                    onClick={() => {
                      onSelect?.(code);
                      onApply?.(code);
                    }}
                  >
                    {busy ? "…" : "Apply"}
                  </button>
                )}
              </div>

              <div className="weu-custom-cart-drawer__coupon-ticket-perforation" aria-hidden>
                <span />
              </div>

              <div className="weu-custom-cart-drawer__coupon-ticket-bottom">
                <p className="weu-custom-cart-drawer__coupon-ticket-desc">
                  {description}
                </p>
                <span className="weu-custom-cart-drawer__coupon-ticket-tag">
                  {code}
                </span>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
